import type { HeroGs1BandStatus, HeroGs1GroupStatuses, HeroGs1LikertStatus, ImageView } from '@vizit/api';
import { AlertExclamationIcon, CheckBoldIcon, HelpCircleIcon, WarningTriangleIcon } from '@vizit/icons';
import type { ComponentType } from 'react';

type IconComponent = ComponentType<{ size?: number; color?: string; className?: string }>;

/** Per-component GS1 statuses as served by the API, keyed by canonical component key. */
type HeroGs1ComponentStatuses = NonNullable<ImageView['hero_gs1_component_statuses']>;

/** Status of a single compliance check. `unscored` when the API has no status for it. */
type RowStatus = 'pass' | 'review' | 'fail' | 'unscored';
/** Worst-of aggregate status for a compliance section. Never `unscored` (sections with only unscored rows have no status). */
type SectionStatus = 'pass' | 'review' | 'fail';
/** Qualitative band for a 0–100 score-bar section (image appeal, layout/staging). */
type LikertStatus = 'low' | 'medium' | 'high';

/** A single labeled compliance check within a rows-based section. */
interface HeroScoringRow {
  /** Human-readable check name (e.g. "Brand", "Product tilt"). */
  label: string;
  /** Pass/review/fail classification, or `unscored` when no score is available. */
  status: RowStatus;
}

/** Section body: either a list of compliance checks (`rows`) or a single 0–100 value for a score bar (`score`). */
type HeroScoringBody =
  | { kind: 'rows'; rows: HeroScoringRow[] }
  | { kind: 'score'; score: number | null; status?: LikertStatus };

/**
 * A caveat to surface above a section's checks. Kept as a discriminant rather than a rendered node so
 * {@link buildHeroScoringSections} stays a pure data function — the panel maps it to a banner at render time.
 *
 * `collection-accuracy`: the hero was scored without a product title, so the 4Ws were inferred from the
 * image instead of matched against a title.
 */
type HeroScoringNotice = 'collection-accuracy';

/** A panel section. `rows` bodies render a collapsible check list; `score` bodies render a likert score bar. */
interface HeroScoringSection {
  /** Stable identifier (e.g. "four_ws", "image_quality"). */
  id: string;
  /** Section heading. */
  title: string;
  /** Optional sub-heading shown under the title. */
  description?: string;
  /** Aggregate status badge for `rows` sections; omit for `score` sections (their status is derived from the score at render time). */
  status?: SectionStatus;
  body: HeroScoringBody;
  /** Whether a `rows` section starts expanded. Ignored for `score` sections. */
  defaultOpen?: boolean;
  /** Caveat rendered above the section's checks. Omit when the section carries no caveat. */
  notice?: HeroScoringNotice;
}

/** Props for {@link HeroScoringPanel}. */
interface HeroScoringPanelProps {
  /** Ordered sections to render, typically produced by {@link buildHeroScoringSections}. */
  sections: HeroScoringSection[];
  /** `agent_ready` from the API. Anything but `true` reads as not ready. */
  agentReady?: boolean | null;
  /** `mobile_ready` from the API. Anything but `true` reads as not ready. */
  mobileReady?: boolean | null;
  /** Optional class merged onto the panel container. */
  className?: string;
}

/** Raw 0–100 (or null) scores keyed by metric, as returned by the scoring backend. Input to {@link buildHeroScoringSections}. */
interface HeroScoreBreakdown {
  'four_ws.brand'?: number | null;
  'four_ws.product_type'?: number | null;
  'four_ws.variant'?: number | null;
  'four_ws.size_and_count'?: number | null;
  'image_quality.background_separation'?: number | null;
  'image_quality.product_centered'?: number | null;
  'image_quality.product_prominent'?: number | null;
  'image_quality.product_cropping'?: number | null;
  'image_quality.product_tilt'?: number | null;
  'image_quality.no_off_pack_text'?: number | null;
  /** L2 audience model on the colour frame ×100. Drives the "Composition" tile. */
  composition?: number | null;
  /** L2 audience model on the greyscale frame ×100. Drives the "Product Staging" tile. */
  layout_staging?: number | null;
  /** Bedrock VLM aesthetic score. Present in the payload but intentionally not surfaced — it is not "composition". */
  image_appeal?: number | null;
}

/**
 * Maps a per-component API status to a {@link RowStatus}. A missing status renders as unscored.
 */
function toRowStatus(status: HeroGs1BandStatus | HeroGs1LikertStatus | null | undefined): RowStatus {
  switch (status) {
    case 'PASS':
      return 'pass';
    case 'REVIEW':
      return 'review';
    case 'FAIL':
      return 'fail';
    default:
      return 'unscored';
  }
}

/**
 * Maps a grouped API band status to a {@link SectionStatus}. Undefined when the group has no verdict.
 */
function toSectionStatus(status: HeroGs1BandStatus | null | undefined): SectionStatus | undefined {
  switch (status) {
    case 'PASS':
      return 'pass';
    case 'REVIEW':
      return 'review';
    case 'FAIL':
      return 'fail';
    default:
      return undefined;
  }
}

/**
 * Maps a grouped API likert status to a {@link LikertStatus}. Undefined when the axis is unscored.
 */
function toLikertStatus(status: HeroGs1LikertStatus | null | undefined): LikertStatus | undefined {
  switch (status) {
    case 'HIGH':
      return 'high';
    case 'MEDIUM':
      return 'medium';
    case 'LOW':
      return 'low';
    default:
      return undefined;
  }
}

const ROW_STATUS_ICON: Record<RowStatus, { Icon: IconComponent; colorClass: string }> = {
  pass: { Icon: CheckBoldIcon, colorClass: 'text-success-600' },
  review: { Icon: WarningTriangleIcon, colorClass: 'text-warning-500' },
  fail: { Icon: AlertExclamationIcon, colorClass: 'text-error-500' },
  unscored: { Icon: HelpCircleIcon, colorClass: 'text-quaternary' },
};

type StatusBadgeStatus = SectionStatus | LikertStatus;

const STATUS_BADGE: Record<StatusBadgeStatus, { label: string; className: string }> = {
  pass: { label: 'PASS', className: 'bg-success-100 text-success-700' },
  review: { label: 'REVIEW', className: 'bg-warning-100 text-warning-700' },
  fail: { label: 'FAIL', className: 'bg-error-100 text-error-700' },
  high: { label: 'HIGH', className: 'bg-success-100 text-success-700' },
  medium: { label: 'MED', className: 'bg-yellow-100 text-yellow-700' },
  low: { label: 'LOW', className: 'bg-error-100 text-error-700' },
};

export { ROW_STATUS_ICON, STATUS_BADGE, toRowStatus, toSectionStatus, toLikertStatus };
export type {
  RowStatus,
  SectionStatus,
  LikertStatus,
  StatusBadgeStatus,
  HeroGs1ComponentStatuses,
  HeroGs1GroupStatuses,
  HeroScoringRow,
  HeroScoringBody,
  HeroScoringNotice,
  HeroScoringSection,
  HeroScoringPanelProps,
  HeroScoreBreakdown,
};
